import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';

import type {
  FormFieldDefinition,
  FormFieldValue,
  FormSubmissionResponse,
  FormSubmissionValues,
  FormTemplateResponse,
} from '@emr/shared';

import {
  createFormSubmission,
  deleteFormSubmission,
  fetchFormSubmissions,
  fetchFormTemplate,
  fetchFormTemplates,
} from '../api/forms.ts';
import { Button, ErrorText, Panel } from '../components/primitives.tsx';
import { GatedRoute } from '../components/GatedRoute.tsx';
import { QuerySection } from '../components/QuerySection.tsx';
import { formatDateTime } from '../lib/dateFormat.ts';

/**
 * The form-fill surface, reachable from a patient's chart tabs (`BACKLOG.md` C10b). `chart:write` to
 * fill or delete a submission, `chart:read` to view — matching `problemsRoutes.ts`'s own
 * capability split, gated server-side by `evaluateChartAccess` regardless of what this page shows.
 */
export function PatientFormsPage() {
  const { t } = useTranslation('forms');
  const { patientId } = useParams<{ patientId: string }>();

  if (patientId === undefined) {
    return <p className="m-0 text-sm text-danger">{t('common.noPatientId')}</p>;
  }

  return (
    <GatedRoute title={t('submissions.pageTitle')} capability="chart:write" feature={null}>
      <FormsSection patientId={patientId} />
    </GatedRoute>
  );
}

function FormsSection({ patientId }: { readonly patientId: string }) {
  const { t } = useTranslation('forms');
  const queryClient = useQueryClient();
  const queryKey = ['patient', patientId, 'forms', 'submissions'];

  const submissionsQuery = useQuery<readonly FormSubmissionResponse[]>({
    queryKey,
    queryFn: ({ signal }) => fetchFormSubmissions(patientId, signal),
  });

  const templatesQuery = useQuery<readonly FormTemplateResponse[]>({
    queryKey: ['form-templates'],
    queryFn: ({ signal }) => fetchFormTemplates(signal),
  });

  const [selectedTemplateId, setSelectedTemplateId] = useState('');
  const effectiveTemplateId =
    selectedTemplateId === '' ? (templatesQuery.data?.[0]?.id ?? '') : selectedTemplateId;

  const templateQuery = useQuery<FormTemplateResponse>({
    queryKey: ['form-templates', effectiveTemplateId],
    queryFn: ({ signal }) => fetchFormTemplate(effectiveTemplateId, signal),
    enabled: effectiveTemplateId !== '',
  });

  const remove = useMutation({
    mutationFn: (submissionId: string) => deleteFormSubmission(patientId, submissionId),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey });
    },
  });

  return (
    <div className="flex flex-col gap-4">
      <p className="m-0 text-sm text-ink-muted">{t('submissions.description')}</p>

      <Panel title={t('submissions.fillTitle')}>
        {templatesQuery.isPending ? (
          <p className="m-0 text-sm text-ink-muted">{t('common.loading')}</p>
        ) : (templatesQuery.data ?? []).length === 0 ? (
          <p className="m-0 text-sm text-ink-muted">{t('submissions.noTemplates')}</p>
        ) : (
          <div className="flex flex-col gap-4">
            <label className="flex flex-col gap-1.5">
              <span className="text-sm font-medium text-ink">{t('submissions.templateLabel')}</span>
              <select
                value={effectiveTemplateId}
                onChange={(event) => {
                  setSelectedTemplateId(event.target.value);
                }}
                className="rounded-control border border-field-line bg-field px-3 py-2 text-base text-ink sm:max-w-sm sm:text-sm"
              >
                {(templatesQuery.data ?? []).map((template) => (
                  <option key={template.id} value={template.id}>
                    {template.name}
                  </option>
                ))}
              </select>
            </label>
            {templateQuery.data !== undefined ? (
              <FillForm
                key={templateQuery.data.id}
                patientId={patientId}
                template={templateQuery.data}
                onSubmitted={() => {
                  void queryClient.invalidateQueries({ queryKey });
                }}
              />
            ) : null}
          </div>
        )}
      </Panel>

      <Panel title={t('submissions.pastTitle')}>
        <QuerySection
          query={submissionsQuery}
          loadingLabel={t('common.loading')}
          isEmpty={(data) => data.length === 0}
          emptyMessage={t('submissions.emptyMessage')}
        >
          {(submissions) => (
            <ul className="m-0 flex list-none flex-col gap-3 p-0">
              {submissions.map((submission) => (
                <SubmissionCard
                  key={submission.id}
                  submission={submission}
                  onDelete={() => {
                    remove.mutate(submission.id);
                  }}
                  deletePending={remove.isPending}
                />
              ))}
            </ul>
          )}
        </QuerySection>
      </Panel>
    </div>
  );
}

/** One past submission, rendered against the *exact* template version it was filled against — a
 * direct `GET /api/form-templates/:templateId` by this submission's own pinned id, never the
 * family's current version, which is what proves the versioning guarantee in the UI: an old
 * submission keeps reading correctly no matter how many times the template has since been edited. */
function SubmissionCard({
  submission,
  onDelete,
  deletePending,
}: {
  readonly submission: FormSubmissionResponse;
  readonly onDelete: () => void;
  readonly deletePending: boolean;
}) {
  const { t } = useTranslation('forms');
  const templateQuery = useQuery<FormTemplateResponse>({
    queryKey: ['form-templates', submission.templateId],
    queryFn: ({ signal }) => fetchFormTemplate(submission.templateId, signal),
  });

  const fieldsByKey = new Map(
    (templateQuery.data?.fields ?? []).map((field) => [field.key, field]),
  );

  return (
    <li className="rounded-control border border-line p-3 text-sm">
      <div className="flex flex-wrap items-baseline justify-between gap-2">
        <span className="font-medium text-ink">
          {templateQuery.data?.name ?? t('submissions.loadingTemplate')}
          {templateQuery.data !== undefined
            ? ` (${t('templates.versionBadge', { version: templateQuery.data.version })})`
            : ''}
        </span>
        <span className="text-ink-muted">{formatDateTime(submission.submittedAt)}</span>
      </div>
      <dl className="m-0 mt-2 flex flex-col gap-1">
        {Object.entries(submission.values).map(([key, value]) => (
          <div key={key} className="flex flex-col gap-0.5 sm:flex-row sm:gap-2">
            <dt className="text-ink-muted sm:w-48 sm:shrink-0">
              {fieldsByKey.get(key)?.label ?? key}
            </dt>
            <dd className="m-0 text-ink break-words [overflow-wrap:anywhere]">
              {formatFieldValue(value)}
            </dd>
          </div>
        ))}
      </dl>
      <div className="mt-2">
        <Button variant="secondary" pending={deletePending} onClick={onDelete}>
          {t('submissions.deleteButton')}
        </Button>
      </div>
    </li>
  );
}

function formatFieldValue(value: FormFieldValue): string {
  if (value === null) return '—';
  if (typeof value === 'boolean') return value ? '✓' : '—';
  return String(value);
}

function FillForm({
  patientId,
  template,
  onSubmitted,
}: {
  readonly patientId: string;
  readonly template: FormTemplateResponse;
  readonly onSubmitted: () => void;
}) {
  const { t } = useTranslation('forms');
  const [values, setValues] = useState<FormSubmissionValues>({});

  const submit = useMutation({
    mutationFn: () =>
      createFormSubmission(patientId, {
        templateId: template.id,
        encounterId: null,
        values,
        confidentialityReasons: [],
      }),
    onSuccess: () => {
      setValues({});
      onSubmitted();
    },
  });

  return (
    <form
      onSubmit={(event) => {
        event.preventDefault();
        submit.mutate();
      }}
      className="flex flex-col gap-3 border-t border-row-line pt-4"
    >
      {template.fields.map((field) => (
        <FieldInput
          key={field.key}
          field={field}
          value={values[field.key] ?? null}
          onChange={(next) => {
            setValues((current) => ({ ...current, [field.key]: next }));
          }}
        />
      ))}
      <div>
        <Button type="submit" pending={submit.isPending}>
          {t('submissions.submitButton')}
        </Button>
      </div>
      {submit.isError ? <ErrorText message={t('submissions.submitError')} /> : null}
    </form>
  );
}

function FieldInput({
  field,
  value,
  onChange,
}: {
  readonly field: FormFieldDefinition;
  readonly value: FormFieldValue;
  readonly onChange: (value: FormFieldValue) => void;
}) {
  const controlClass =
    'w-full rounded-control border border-field-line bg-field px-3 py-2 text-base text-ink sm:max-w-sm sm:text-sm';

  return (
    <label className="flex flex-col gap-1.5">
      <span className="text-sm font-medium text-ink">
        {field.label}
        {field.required ? ' *' : ''}
      </span>
      {field.helpText !== null ? (
        <span className="text-sm text-ink-muted">{field.helpText}</span>
      ) : null}

      {field.type === 'text' ? (
        <input
          type="text"
          className={controlClass}
          maxLength={field.maxLength ?? undefined}
          required={field.required}
          value={typeof value === 'string' ? value : ''}
          onChange={(event) => {
            onChange(event.target.value);
          }}
        />
      ) : null}

      {field.type === 'choice' ? (
        <select
          className={controlClass}
          required={field.required}
          value={typeof value === 'string' ? value : ''}
          onChange={(event) => {
            onChange(event.target.value === '' ? null : event.target.value);
          }}
        >
          <option value="" />
          {field.choices.map((choice) => (
            <option key={choice} value={choice}>
              {choice}
            </option>
          ))}
        </select>
      ) : null}

      {field.type === 'numeric' ? (
        <input
          type="number"
          className={controlClass}
          min={field.min ?? undefined}
          max={field.max ?? undefined}
          required={field.required}
          value={typeof value === 'number' ? value : ''}
          onChange={(event) => {
            onChange(event.target.value === '' ? null : Number(event.target.value));
          }}
        />
      ) : null}

      {field.type === 'date' ? (
        <input
          type="date"
          className={controlClass}
          required={field.required}
          value={typeof value === 'string' ? value : ''}
          onChange={(event) => {
            onChange(event.target.value === '' ? null : event.target.value);
          }}
        />
      ) : null}

      {field.type === 'checkbox' ? (
        <input
          type="checkbox"
          checked={value === true}
          onChange={(event) => {
            onChange(event.target.checked);
          }}
        />
      ) : null}
    </label>
  );
}
